import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, CalendarIcon, MapPinIcon, ClockIcon, UsersIcon, SparklesIcon, MailCheckIcon, GlobeIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { GivebutterDonate } from '../components/GivebutterDonate';
const SANDOVAL_IMAGE = 'https://cdn.magicpatterns.com/uploads/41ina9BJwrzGYJTD1injLe/NeidaS.jpg';
const ZUNIGA_IMAGE = 'https://cdn.magicpatterns.com/uploads/sCwWAyRQF74f8hLN1q2Rrm/JoseZ.jpg';
const ALVAREZ_IMAGE = 'https://cdn.magicpatterns.com/uploads/bmHrNj6MWDZD2JVFmaG48J/Copan_Alvarez.jpg';
const FLYER_IMAGE = 'https://cdn.magicpatterns.com/uploads/fdgKGqY2axfX3n3ihCbwUD/Voices_of_Impact_-_New_York.jpg';
const NY_BACKDROP = 'https://cdn.magicpatterns.com/patterns/generated-images/6004ba36-767f-4fce-adb9-f35b03fce46c.jpg';
const DETAIL_ICONS = [CalendarIcon, ClockIcon, MapPinIcon, UsersIcon];
const COPY = {
  EN: {
    back: 'Back to site',
    eyebrow: 'Hispanic Heritage Month · New York City',
    subtitle: 'A Hispanic Heritage Month Celebration',
    details: [{
      label: 'Date',
      value: 'Thursday, October 1'
    }, {
      label: 'Time',
      value: '5:00 – 8:00 PM'
    }, {
      label: 'Venue',
      value: '30 Rockefeller Plaza, New York, NY'
    }, {
      label: 'Format',
      value: 'Evening reception'
    }],
    flyerAria: 'View the full invitation graphic',
    flyerAlt: 'Voices of Impact — A Hispanic Heritage Month Celebration, Thursday, October 1, 30 Rockefeller Plaza, New York, 5–8 PM',
    rsvpTitle: 'Invited Guest RSVP',
    rsvpText: 'Guests who have received a formal invitation may confirm their attendance below.',
    guestsEyebrow: 'Special Guests',
    sandovalAlt: 'Voices of Impact special guest Neida Sandoval',
    zunigaAlt: 'Voices of Impact special guest José Zuñiga',
    alvarezAlt: 'Voices of Impact special guest Copán Álvarez',
    guestsHeading: 'Joining Us That Evening',
    guestsIntro: "We are honored to welcome three distinguished Honduran voices whose work in journalism, the arts, and business leadership has carried our community's story to national audiences.",
    alvarezRole: 'Journalist & Business Leader',
    alvarezBio: 'A world-renowned Honduran sports journalist, Copán Álvarez has covered the biggest stages in international sport — World Cups, Olympic Games, and championship fights — bringing the passion of Honduran and Latin American athletics to audiences across the Americas. Beyond the broadcast booth, he has used his platform to open doors for Honduran and Latino professionals in an industry where they have long been underrepresented, and to champion the next generation of storytellers.',
    sandovalRole: 'Journalist & Broadcast Anchor',
    sandovalBio: 'A Honduran-born journalist with more than three decades in Spanish-language television, Neida Sandoval became one of the most recognized faces in U.S. Hispanic media as a longtime national morning-show anchor. Her reporting has taken her from natural disasters and immigration stories to interviews with heads of state, earning her Emmy recognition and a reputation for bringing Central American stories to audiences that rarely heard them.',
    zunigaRole: 'Actor, Film & Television',
    zunigaBioLead: 'Born in Honduras and raised in the United States, Jose Zuniga has built a decades-long career across film, television, and the New York stage. His film work includes ',
    zunigaBioMid: ', with television credits spanning ',
    zunigaBioEnd: '. His body of work has helped broaden how Latino characters are written and portrayed on screen.',
    aboutEyebrow: 'About the Evening',
    aboutHeading: 'A Room You Will Want to Be In',
    aboutParagraphs: ['Designed to create meaningful connections across industries and generations, Voices of Impact offers guests the opportunity to expand their networks, exchange ideas, discover new opportunities, and build relationships with individuals who are making an impact in their communities and beyond.', 'Throughout the evening, guests will also hear from distinguished special guests and inspiring voices whose leadership reflects the growing influence and contributions of the Honduran-American and broader Latino community in the United States.', 'Whether you are looking to meet fellow leaders, develop new partnerships, connect with emerging talent, or simply become part of a growing national network, Voices of Impact is a room you will want to be in.'],
    highlights: [{
      title: 'A room built for connection',
      text: 'Leaders, executives, entrepreneurs, professionals, public servants, cultural figures, and changemakers from across the country, gathered in one place.'
    }, {
      title: 'Voices worth hearing',
      text: 'Distinguished special guests and inspiring speakers whose leadership reflects the growing influence of the Honduran-American and broader Latino community.'
    }, {
      title: 'Across industries and generations',
      text: 'Expand your network, exchange ideas, discover opportunities, and meet emerging talent alongside established leaders.'
    }],
    missionHeading: 'Connect. Empower. Celebrate.',
    missionText: 'At the Honduran-American Association, our mission is to Connect, Empower, and Celebrate. This evening is about putting that mission into action — bringing remarkable people together and creating connections that can lead to new ideas, new collaborations, and lasting impact.',
    missionClosing: 'Come ready to connect. Leave with new relationships, new possibilities, and a stronger sense of what we can build together.',
    footer: 'Hosted by the Honduran-American Association'
  },
  ES: {
    back: 'Volver al sitio',
    eyebrow: 'Mes de la Herencia Hispana · Ciudad de Nueva York',
    subtitle: 'Una Celebración del Mes de la Herencia Hispana',
    details: [{
      label: 'Fecha',
      value: 'Jueves, 1 de octubre'
    }, {
      label: 'Hora',
      value: '5:00 – 8:00 P.M.'
    }, {
      label: 'Lugar',
      value: '30 Rockefeller Plaza, Nueva York, NY'
    }, {
      label: 'Formato',
      value: 'Recepción vespertina'
    }],
    flyerAria: 'Ver la invitación completa',
    flyerAlt: 'Voices of Impact — Una Celebración del Mes de la Herencia Hispana, jueves 1 de octubre, 30 Rockefeller Plaza, Nueva York, 5–8 P.M.',
    rsvpTitle: 'Confirmación de Invitados',
    rsvpText: 'Las personas que han recibido una invitación formal pueden confirmar su asistencia a continuación.',
    guestsEyebrow: 'Invitados Especiales',
    sandovalAlt: 'Invitada especial de Voices of Impact, Neida Sandoval',
    zunigaAlt: 'Invitado especial de Voices of Impact, José Zuñiga',
    alvarezAlt: 'Invitado especial de Voices of Impact, Copán Álvarez',
    guestsHeading: 'Nos Acompañan Esa Noche',
    guestsIntro: 'Nos honra dar la bienvenida a tres distinguidas voces hondureñas cuyo trabajo en el periodismo, las artes y el liderazgo empresarial ha llevado la historia de nuestra comunidad a audiencias nacionales.',
    alvarezRole: 'Periodista y Líder Empresarial',
    alvarezBio: 'Periodista deportivo hondureño de renombre mundial, Copán Álvarez ha cubierto los escenarios más grandes del deporte internacional —Copas del Mundo, Juegos Olímpicos y peleas de campeonato— llevando la pasión del deporte hondureño y latinoamericano a audiencias de todo el continente. Más allá de la cabina de transmisión, ha usado su plataforma para abrir puertas a profesionales hondureños y latinos en una industria donde por mucho tiempo han estado subrepresentados, y para impulsar a la próxima generación de narradores.',
    sandovalRole: 'Periodista y Presentadora',
    sandovalBio: 'Periodista nacida en Honduras con más de tres décadas en la televisión en español, Neida Sandoval se convirtió en uno de los rostros más reconocidos de los medios hispanos en Estados Unidos como presentadora de un programa matutino nacional durante muchos años. Su labor periodística la ha llevado desde desastres naturales e historias de inmigración hasta entrevistas con jefes de estado, obteniendo reconocimiento Emmy y la reputación de llevar las historias centroamericanas a audiencias que rara vez las escuchaban.',
    zunigaRole: 'Actor, Cine y Televisión',
    zunigaBioLead: 'Nacido en Honduras y criado en los Estados Unidos, Jose Zuniga ha construido una carrera de décadas en el cine, la televisión y los escenarios de Nueva York. Su trabajo en cine incluye ',
    zunigaBioMid: ', con créditos en televisión que abarcan ',
    zunigaBioEnd: '. Su trayectoria ha ayudado a ampliar la manera en que los personajes latinos son escritos y representados en pantalla.',
    aboutEyebrow: 'Sobre la Velada',
    aboutHeading: 'Una Sala en la Que Querrá Estar',
    aboutParagraphs: ['Diseñada para crear conexiones significativas entre industrias y generaciones, Voices of Impact ofrece a los invitados la oportunidad de ampliar sus redes, intercambiar ideas, descubrir nuevas oportunidades y construir relaciones con personas que están generando impacto en sus comunidades y más allá.', 'A lo largo de la velada, los invitados también escucharán a distinguidos invitados especiales y voces inspiradoras cuyo liderazgo refleja la creciente influencia y las contribuciones de la comunidad hondureño-americana y latina en los Estados Unidos.', 'Ya sea que busque conocer a otros líderes, desarrollar nuevas alianzas, conectar con talento emergente o simplemente formar parte de una red nacional en crecimiento, Voices of Impact es una sala en la que querrá estar.'],
    highlights: [{
      title: 'Una sala hecha para conectar',
      text: 'Líderes, ejecutivos, empresarios, profesionales, servidores públicos, figuras culturales y agentes de cambio de todo el país, reunidos en un mismo lugar.'
    }, {
      title: 'Voces que vale la pena escuchar',
      text: 'Invitados especiales distinguidos y oradores inspiradores cuyo liderazgo refleja la creciente influencia de la comunidad hondureño-americana y latina.'
    }, {
      title: 'Entre industrias y generaciones',
      text: 'Amplíe su red, intercambie ideas, descubra oportunidades y conozca talento emergente junto a líderes consolidados.'
    }],
    missionHeading: 'Conectar. Empoderar. Celebrar.',
    missionText: 'En la Asociación Hondureño-Americana, nuestra misión es Conectar, Empoderar y Celebrar. Esta velada es esa misión en acción: reunir a personas extraordinarias y crear vínculos que puedan dar paso a nuevas ideas, nuevas colaboraciones y un impacto duradero.',
    missionClosing: 'Venga listo para conectar. Váyase con nuevas relaciones, nuevas posibilidades y una idea más clara de lo que podemos construir juntos.',
    footer: 'Organizado por la Asociación Hondureño-Americana'
  }
};
const FILMS = ['Crooklyn', 'Smoke', 'Con Air', 'Alien Resurrection', 'Constantine'];
const SHOWS = ['Law & Order', 'NYPD Blue', 'ER', '24'];
function TitleList({
  titles
}: {
  titles: string[];
}) {
  return <>
      {titles.map((title, i) => <React.Fragment key={title} data-id="element-4070" data-mp="17ovavmfpi32a" data-mpi={i}>
          <span className="text-white/90 italic" data-id="element-4071" data-mp="17s9awo0we06k">{title}</span>
          {i < titles.length - 1 ? ', ' : ''}
        </React.Fragment>)}
    </>;
}
export function VoicesOfImpactNYPage() {
  const {
    language,
    setLanguage
  } = useLanguage();
  const isEs = language === 'ES';
  const c = isEs ? COPY.ES : COPY.EN;
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen w-full bg-[#0b1f3f] text-white font-nunito" data-id="element-4072" data-mp="3iihp4y17hya5">
      <header className="relative z-10 w-full px-6 py-6 sm:px-12 flex items-center justify-between gap-4 border-b border-[#c9a227]/30" data-id="element-4073" data-mp="3ehxqfq0zimlw">
        <a href="#" className="flex items-center gap-3 group" aria-label="Honduran American Association" data-id="element-4074" data-mp="1hicqv59fab7y">
          <img src="https://cdn.magicpatterns.com/uploads/b1GB8Z9aEvoE58p9BdX4qx/HonduranAmericanLogoNew.png" alt="Honduran American Association" className="h-10 w-auto object-contain brightness-0 invert group-hover:opacity-80 transition-opacity" data-id="element-4075" data-mp="1rdwi3ed1klqd" />
        </a>
        <div className="flex items-center gap-3" data-id="element-4076" data-mp="25cvyjyqehubg">
          <button onClick={() => setLanguage(isEs ? 'EN' : 'ES')} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#c9a227] hover:bg-[#dcb63b] text-[#0b1f3f] text-sm font-bold transition-colors" aria-label={isEs ? 'Switch to English' : 'Ver en español'} data-id="element-4077" data-mp="25bkv600vaokv">
            <GlobeIcon className="w-4 h-4" data-id="element-4078" data-mp="2o3x63b5l6y6m" />
            {isEs ? 'English' : 'Español'}
          </button>
          <a href="#" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-sm font-semibold transition-colors" data-id="element-4079" data-mp="chlb8o0hcwuj">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-4080" data-mp="3hgb8yxm90qe" />
            <span className="hidden sm:inline" data-id="element-4081" data-mp="3i878av4l0h7">{c.back}</span>
          </a>
        </div>
      </header>

      <main data-id="element-4082" data-mp="w52qx5k4bvtm">
        {/* Invitation */}
        <section className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 pt-12 lg:pt-16" data-id="element-4083" data-mp="w411tx69p8sw">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-center max-w-3xl mx-auto" data-id="element-4084" data-mp="r5pvvxj3fify">
            <p className="font-nunito text-[#c9a227] text-xs font-bold uppercase tracking-[0.25em] mb-4" data-id="element-4085" data-mp="1a4u1ewbfeu2v">
              {c.eyebrow}
            </p>
            <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl tracking-[0.08em] leading-[0.95] mb-3" data-id="element-4086" data-mp="um164mh7skvh">
              Voices of Impact
            </h1>
            <p className="font-nunito text-white/80 text-sm uppercase tracking-[0.2em]" data-id="element-4087" data-mp="kowfoagc638v">
              {c.subtitle}
            </p>
            <div className="w-20 h-px bg-[#c9a227] mx-auto mt-7" data-id="element-4088" data-mp="posmehgf48s4" />
          </motion.div>

          {/* Info bar */}
          <dl className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-px rounded-2xl overflow-hidden border border-white/10 bg-white/10" data-id="element-4089" data-mp="1ddtwsa8t9u01">
            {c.details.map((item, i) => {
            const Icon = DETAIL_ICONS[i];
            return <div key={item.label} className="bg-[#0b1f3f] px-5 py-5 flex items-start gap-3" data-id="element-4090" data-mp="18mbwloem3xg4" data-mpi={i}>
                  <Icon className="w-4 h-4 text-[#c9a227] mt-0.5 flex-shrink-0" data-id="element-4091" data-mp="1wal9g0tulav4" />
                  <div data-id="element-4092" data-mp="213o98qp7xgjr">
                    <dt className="font-nunito text-white/45 text-[10px] font-bold uppercase tracking-[0.18em] mb-0.5" data-id="element-4093" data-mp="2147gndh8smjj">
                      {item.label}
                    </dt>
                    <dd className="font-nunito text-white text-sm font-semibold leading-snug" data-id="element-4094" data-mp="3gtfaxb8wnrbd">
                      {item.value}
                    </dd>
                  </div>
                </div>;
          })}
          </dl>
        </section>

        <section className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-12 lg:py-16" data-id="element-4095" data-mp="32u60ytbaltfu">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-stretch" data-id="element-4096" data-mp="2xvmbwk1ek1j9">
            {/* Left — flyer */}
            <motion.aside initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: [0.16, 1, 0.3, 1]
          }} className="flex" data-id="element-4097" data-mp="3bvndy2telf0b">
              <a href={FLYER_IMAGE} target="_blank" rel="noopener noreferrer" className="relative block w-full h-full rounded-2xl overflow-hidden shadow-2xl ring-1 ring-[#c9a227]/50 bg-white group" aria-label={c.flyerAria} data-id="element-4098" data-mp="3fxs2v8j15hf1">
                <img src={FLYER_IMAGE} alt={c.flyerAlt} className="w-full h-full object-contain block transition-transform duration-500 group-hover:scale-[1.02]" data-id="element-4099" data-mp="3ty2s674oyny3" />
              </a>
            </motion.aside>

            {/* Right — RSVP */}
            <motion.div initial={{
            opacity: 0,
            y: 24
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1,
            ease: [0.16, 1, 0.3, 1]
          }} className="flex flex-col h-full" data-id="element-4100" data-mp="367qai4jn9cub">
              <div className="rounded-2xl border border-[#c9a227]/40 bg-white/[0.04] overflow-hidden flex flex-col flex-1" data-id="element-4101" data-mp="2axk2u1c9mfch">
                <div className="px-6 pt-6 pb-4" data-id="element-4102" data-mp="260lbtrnweoii">
                  <h2 className="inline-flex items-center gap-2 font-bebas text-2xl tracking-wider mb-1.5" data-id="element-4103" data-mp="20zxk13rui3n8">
                    <MailCheckIcon className="w-5 h-5 text-[#c9a227]" data-id="element-4104" data-mp="1wy27t48dm5uo" />
                    {c.rsvpTitle}
                  </h2>
                  <p className="font-nunito text-white/65 text-sm leading-relaxed" data-id="element-4105" data-mp="1s1ln6k0dy9mc">
                    {c.rsvpText}
                  </p>
                </div>
                <div className="bg-white p-4 sm:p-5 flex-1 flex flex-col justify-center" data-id="element-4106" data-mp="1i9fu16yedm5k">
                  <GivebutterDonate data-id="element-4107" data-mp="1dbmomg4x4w6c" />
                  {React.createElement('givebutter-widget', {
                  id: 'LylV7o'
                })}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Special guests */}
        <section className="border-t border-white/10" data-id="element-4108" data-mp="2s74g897qr723">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-16 lg:py-20" data-id="element-4109" data-mp="2s7nn8uvusb1c">
            <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-4110" data-mp="2x55wynhp2uae">
              <p className="inline-flex items-center gap-2 font-nunito text-[#c9a227] text-xs font-bold uppercase tracking-[0.25em] mb-5" data-id="element-4111" data-mp="2j6ps723e7duw">
                <SparklesIcon className="w-4 h-4" data-id="element-4112" data-mp="2n6qbb01fsd8a" />
                {c.guestsEyebrow}
              </p>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-wider leading-tight mb-4" data-id="element-4113" data-mp="29f1y0tfgbipq">
                {c.guestsHeading}
              </h2>
              <div className="w-16 h-px bg-[#c9a227] mx-auto mb-7" data-id="element-4114" data-mp="1ziqhtscqq8q0" />
              <p className="font-nunito text-white/80 text-base sm:text-lg leading-relaxed" data-id="element-4115" data-mp="1zgdsgu4nkw2x">
                {c.guestsIntro}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-10" data-id="element-4116" data-mp="1qitqfiyyuvr9">
              {[{
              name: 'Neida Sandoval',
              image: SANDOVAL_IMAGE,
              alt: c.sandovalAlt,
              role: c.sandovalRole,
              bio: <>{c.sandovalBio}</>
            }, {
              name: 'José Zuñiga',
              image: ZUNIGA_IMAGE,
              alt: c.zunigaAlt,
              role: c.zunigaRole,
              bio: <>
                      {c.zunigaBioLead}
                      <TitleList titles={FILMS} data-id="element-4117" data-mp="1usmq2jlw7kr5" />
                      {c.zunigaBioMid}
                      <TitleList titles={SHOWS} data-id="element-4118" data-mp="1ptte4vm981t3" />
                      {c.zunigaBioEnd}
                    </>
            }, {
              name: 'Copán Álvarez',
              image: ALVAREZ_IMAGE,
              alt: c.alvarezAlt,
              role: c.alvarezRole,
              bio: <>{c.alvarezBio}</>
            }].map((guest, i) => <motion.article key={guest.name} initial={{
              opacity: 0,
              y: 24
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-80px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.1,
              ease: [0.16, 1, 0.3, 1]
            }} className="flex flex-col" data-id="element-4119" data-mp="28mxa4nmy2qqv" data-mpi={i}>
                  <div className="rounded-2xl overflow-hidden border border-[#c9a227]/40 shadow-2xl bg-white" data-id="element-4120" data-mp="2c3e42ezgwq1d">
                    <img src={guest.image} alt={guest.alt} className="w-full h-auto object-contain block" data-id="element-4121" data-mp="27c5rja2ucojz" />
                  </div>
                  <div className="pt-7" data-id="element-4122" data-mp="1eoryvo66ef5o">
                    <h3 className="font-bebas text-3xl tracking-wider mb-1.5" data-id="element-4123" data-mp="1elw4uaxg8ami">
                      {guest.name}
                    </h3>
                    <p className="font-nunito text-[#c9a227] text-[11px] font-bold uppercase tracking-[0.18em] mb-4" data-id="element-4124" data-mp="3v2lqen69vuwx">
                      {guest.role}
                    </p>
                    <p className="font-nunito text-white/70 text-sm leading-relaxed" data-id="element-4125" data-mp="3lc8vc10u0jcj">
                      {guest.bio}
                    </p>
                  </div>
                </motion.article>)}
            </div>

          </div>
        </section>

        {/* About the evening — New York backdrop */}
        <section className="relative border-t border-white/10 overflow-hidden" data-id="element-4126" data-mp="3waj49ao5xx5">
          <img src={NY_BACKDROP} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" data-id="element-4127" data-mp="8uu6me4wvhrm" />
          <div className="absolute inset-0 bg-[#0b1f3f]/55" data-id="element-4128" data-mp="2okovqqxksk0u" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0b1f3f] via-[#0b1f3f]/20 to-[#0b1f3f]" data-id="element-4129" data-mp="2tm4e2b8dccfj" />

          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-20 lg:py-24" data-id="element-4130" data-mp="z3jih08nxizq">
            <div className="text-center max-w-3xl mx-auto" data-id="element-4131" data-mp="u6t3kg8lgtfw">
              <p className="inline-flex items-center gap-2 font-nunito text-[#c9a227] text-xs font-bold uppercase tracking-[0.25em] mb-5" data-id="element-4132" data-mp="pf1j8ng17jy7">
                <SparklesIcon className="w-4 h-4" data-id="element-4133" data-mp="lcen4acbvcv7" />
                {c.aboutEyebrow}
              </p>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-wider leading-tight mb-4" data-id="element-4134" data-mp="gf66ie6tvgw2">
                {c.aboutHeading}
              </h2>
              <div className="w-16 h-px bg-[#c9a227] mx-auto mb-8" data-id="element-4135" data-mp="6g77lnbv6zm9" />
            </div>
            <div className="space-y-5 font-nunito text-white/80 text-base sm:text-lg leading-relaxed text-center max-w-3xl mx-auto" data-id="element-4136" data-mp="6i9x8693uqqg">
              {c.aboutParagraphs.map(text => <p key={text} data-id="element-4137" data-mp="20tf55mr85pbl">{text}</p>)}
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-14" data-id="element-4138" data-mp="1wtxzotbsss6f">
              {c.highlights.map((item, i) => <motion.div key={item.title} initial={{
              opacity: 0,
              y: 24
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.1,
              ease: [0.16, 1, 0.3, 1]
            }} className="rounded-2xl border border-white/15 bg-[#0b1f3f]/70 backdrop-blur-sm p-7 text-center" data-id="element-4139" data-mp="3epq9do6p7w3b" data-mpi={i}>
                  <div className="w-10 h-px bg-[#c9a227] mb-5 mx-auto" data-id="element-4140" data-mp="39sgbrl95oe4e" />
                  <h3 className="font-bebas text-2xl tracking-wider mb-3" data-id="element-4141" data-mp="k0t8lzl737bw">
                    {item.title}
                  </h3>
                  <p className="font-nunito text-white/70 text-sm leading-relaxed" data-id="element-4142" data-mp="oz35cemkifzm">
                    {item.text}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* Mission */}
        <section className="border-t border-white/10 bg-white/[0.03]" data-id="element-4143" data-mp="wkp43635vrbr">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center" data-id="element-4144" data-mp="siv6x7s6d5gc">
            <h2 className="font-bebas text-3xl sm:text-4xl tracking-wider leading-tight mb-5" data-id="element-4145" data-mp="sky88nys2l0d">
              {c.missionHeading}
            </h2>
            <p className="font-nunito text-white/75 text-base sm:text-lg leading-relaxed mb-4" data-id="element-4146" data-mp="11kbhbfizp7kk">
              {c.missionText}
            </p>
            <p className="font-nunito text-white text-base sm:text-lg font-semibold leading-relaxed" data-id="element-4147" data-mp="1g7mqjarp8fhi">
              {c.missionClosing}
            </p>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10 py-8 text-center" data-id="element-4148" data-mp="1p9dx0hu2cqcf">
        <p className="font-nunito text-white/45 text-xs uppercase tracking-[0.2em]" data-id="element-4149" data-mp="1kb2nk87okhuq">
          {c.footer}
        </p>
      </footer>
    </div>;
}